<template>
  <div class="pt-2">
    <!-- Floating Action Bar for bulk actions -->
    <FloatingActionBar
      :selected="selected"
      :totalItems="totalItems"
      :showDeleteSelected="true"
      :show-assign="false"
      :showDeleteAll="false"
      :showUpdateStatus="false"
      :showSelectAll="false"
      @delete-selected="handleShowDialogDelete"
    />
    <div
      class="justify-end items-center"
      :class="[$vuetify.breakpoint.lgAndUp ? 'flex justify-end' : 'flex-col']"
      v-if="!showRefillInventory"
    >
      <div
        class="justify-end mr-4"
        :class="[$vuetify.breakpoint.lgAndUp ? 'flex items-center' : 'flex-col']"
      >
        <v-col
          class="colom"
          :cols="$vuetify.breakpoint.lgAndUp ? '' : '12'"
          :class="[$vuetify.breakpoint.lgAndUp ? '' : 'ml-auto']"
        >
          <v-text-field
            v-model="filterPartName"
            label="Part Name/Part Number"
            solo
            flat
            clearable
            prepend-inner-icon="mdi-magnify"
            @input="handleInput"
            outlined
            dense
            hide-details
          ></v-text-field>
        </v-col>
        <v-col
          class="colom"
          :cols="$vuetify.breakpoint.lgAndUp ? '' : '12'"
          :class="[$vuetify.breakpoint.lgAndUp ? '' : 'ml-auto']"
        >
          <v-text-field
            v-model="filterOrder"
            label="Order Number"
            solo
            flat
            clearable
            prepend-inner-icon="mdi-magnify"
            @input="handleInput"
            outlined
            dense
            hide-details
          ></v-text-field>
        </v-col>
        <v-col
          class="colom"
          :cols="$vuetify.breakpoint.lgAndUp ? '' : '12'"
          :class="[$vuetify.breakpoint.lgAndUp ? '' : 'ml-auto']"
        >
          <v-select
            v-model="selectedOrderStatus"
            label="Select Status"
            :items="inventoryOrderOptions"
            item-value="value"
            item-text="label"
            solo
            flat
            clearable
            @input="handleInput"
            outlined
            dense
            hide-details
          ></v-select>
        </v-col>
      </div>
      <!-- Button removed from here - now managed by parent -->
    </div>
    <div v-if="showTable" class="overflow-x-auto overflow-y-auto pt-2">
      <v-data-table
        :headers="headers"
        :items="inventoryOrders"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
        :server-items-length="totalItems"
        :loading="loading"
        :single-expand="singleExpand"
        :expanded.sync="expanded"
        class="border border-[#e0e0e0] table-radius"
        show-expand
        item-key="_id"
        show-select
        v-model="selected"
        fixed-header
      >
        <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
          <p
            :key="index"
            class="font-title"
            v-if="header.text?.length > 15"
            v-tooltip="{ text: header.text }"
          >
            {{ header.text }}
          </p>
          <p :key="index" v-else class="font-title">{{ header.text }}</p>
        </template>
        <template v-slot:item.partName="{ item }">
          <td class="px-4 py-6 table-font">
            {{ item.product?.productName }}
          </td>
        </template>
        <template v-slot:item.modelNumber="{ item }">
          <td class="px-4 table-font">
            {{ item.product?.modelNumber }}
          </td>
        </template>
        <template v-slot:item.partnerName="{ item }">
          <td class="px-4 table-font">{{ item.partner?.partnerName }}</td>
        </template>
        <template v-slot:item.orderNumber="{ item }">
          <td class="px-4 table-font">{{ item.orderNumber }}</td>
        </template>
        <template v-slot:item.costPrice="{ item }">
          <td class="px-4 table-font">{{ item.costPricePerUnit }}</td>
        </template>
        <template v-slot:item.totalAmount="{ item }">
          <td class="px-4 table-font">{{ item.totalOrderAmount }}</td>
        </template>
        <template v-slot:item.orderStatus="{ item }">
          <OrderStatuses
            :orderStatus="item.orderStatus"
            :statuses="inventoryOrderOptions"
            @statusChange="(status) => onStatusChange(status, item._id)"
          />
        </template>
        <template v-slot:item.updatedBy="{ item }">
          <td class="px-4 table-font">
            {{ item.updatedBy.firstName }} {{ item.updatedBy.lastName }}
          </td>
        </template>
        <template v-slot:item.action="{ item }">
          <td class="px-4">
            <action-button
              :item="item"
              :canEdit="canEdit"
              :canDelete="false"
              :handleEdit="() => orderEdit(item)"
              :showDeleteButton="false"
              :showEditButton="canEdit"
              :isButtonDisabled="disableActionBtn(item)"
            />
          </td>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <v-data-table
              class="expanded-table bg-gray-100 dark-bg-default p-7"
              :headers="expandedHeaders"
              :items="item.orderDeliveryLocation"
              :items-per-page="5"
              hide-default-footer
            >
              <template v-slot:item.storeLocation.storeName="{ item }">
                <td class="px-4">{{ item.storeLocation.storeName }}</td>
              </template>
              <template v-slot:item.orderedQuantity="{ item }">
                <td class="px-4">{{ item.orderedQuantity }}</td>
              </template>
              <template v-slot:item.inStoreOrderStatus="{ item }">
                <td class="px-4" :class="getStoreOrderStatusClr(item.inStoreOrderStatus)">
                  {{ item.inStoreOrderStatus }}
                </td>
              </template>
              <template v-slot:item.storeLocation.storeLocation="{ item }">
                <td class="px-4">
                  {{ Object.values(item.storeLocation.storeLocation).join(', ') }}
                </td>
              </template>
            </v-data-table>
          </td>
        </template>
      </v-data-table>
    </div>
    <div v-else-if="showRefillInventory">
      <AddOrder @cancel="toggleShowTable" :editData="selectedOrderData" />
    </div>
    <AlertPopUp
      :show-confirmation-dialog.sync="showDeleteConfirmation"
      :confirmation-title="'Delete Store'"
      :confirmation-message="`Are you sure you want to delete the store '${storeToDelete?.storeName}'?`"
      :confirmation-action-text="'Delete'"
      :confirmation-action-color="'error'"
      :perform-action="performDeleteStore"
      :cancel="cancelDelete"
      :loading="deleteLoading"
    />

    <!-- Bulk Delete Confirmation Dialog -->
    <AlertPopUp
      :showConfirmationDialog="showDeleteDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="`Do you want to delete selected orders (${selected.length})?`"
      :confirmationActionText="'DELETE'"
      :confirmationActionColor="'red'"
      :performAction="deleteSelectedOrders"
      :cancel="handleShowDialogDelete"
      :loading="deleteLoading"
    />
  </div>
</template>

<script>
import AddOrder from '~/components/Workforce/InventorySettings/AddOrder.vue'
import ActionButton from '@/components/ActionButton.vue'
import { debounce } from '@/utils/common'
import OrderStatuses from './OrderStatuses.vue'
import AlertPopUp from '@/components/AlertPopUp.vue'
import FloatingActionBar from '../CollectionManagement/CaseModules/FloatingActionBar.vue'
import { hasPermission } from '@/utils/common'
import permissionData from '@/utils/permissions'
import { axiosUrl } from '@/utils/workforceApi'
export default {
  name: 'InventoryIn',
  components: {
    AddOrder,
    ActionButton,
    AlertPopUp,
    FloatingActionBar,
    OrderStatuses
  },
  data() {
    return {
      userData: this.$store.state.wfmPermissions,
      options: { itemsPerPage: 5, page: 1 },
      totalItems: 0,
      loading: true,
      inventoryOrders: [],
      showTable: true,
      singleExpand: true,
      inventoryOrderOptions: [
        { label: 'Placed', value: 'PLACED' },
        { label: 'Completed', value: 'COMPLETED' },
        { label: 'In-Progress', value: 'IN_PROGRESS' },
        { label: 'Return', value: 'RETURN' }
      ],
      expandedHeaders: [
        {
          text: 'Store Name',
          value: 'storeLocation.storeName',
          sortable: false
        },
        {
          text: 'Store Address',
          value: 'storeLocation.storeLocation',
          sortable: false
        },
        { text: 'Quantity', value: 'orderedQuantity', sortable: false },
        { text: 'Store Status', value: 'inStoreOrderStatus', sortable: false }
      ],
      filterPartName: '',
      filterOrder: '',
      selectedOrderData: null,
      showRefillInventory: false,
      selectedOrderStatus: '',
      expanded: [],
      // For FloatingActionBar
      selected: [],
      showDeleteDialog: false,
      deleteLoading: false,
      // For AlertPopUp
      showDeleteConfirmation: false,
      storeToDelete: null
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchInventoryOrder()
      },
      deep: true
    }
  },
  computed: {
    headers() {
      const headers = [
        { text: 'Spare Part', value: 'partName', sortable: false },
        { text: 'Part/Model No.', value: 'modelNumber', sortable: false },
        { text: 'Brand Name', value: 'partnerName', sortable: false },
        { text: 'Order No.', value: 'orderNumber', sortable: false },
        { text: 'Cost Price(Per Unit)', value: 'costPrice', sortable: false },
        { text: 'Total Amt.', value: 'totalAmount', sortable: false },
        { text: 'Status', value: 'orderStatus', sortable: false },
        { text: 'Updated By', value: 'updatedBy', sortable: false },
        this.isActionEnable ? { text: 'Action', value: 'action', sortable: false } : []
      ]
      return headers
    },
    canDelete() {
      return hasPermission(this.userData, permissionData.inventoryDelete)
    },
    canEdit() {
      return hasPermission(this.userData, permissionData.inventoryEdit)
    },
    canAdd() {
      return hasPermission(this.userData, permissionData.inventoryWrite)
    },
    isActionEnable() {
      return this.canEdit || this.canDelete
    }
  },
  methods: {
    getStoreOrderStatusClr(status) {
      const statuses = {
        PLACED: 'text-sky-500',
        CANCELLED: 'text-red-500',
        IN_PROGRESS: 'text-blue-500',
        COMPLETED: 'text-green-500',
        RETURNED: 'text-yellow-500'
      }

      return statuses[status]
    },
    disableActionBtn(item) {
      return item.orderStatus === 'COMPLETED' || (!this.canEdit && !this.canDelete)
    },
    toggleShowTable() {
      this.showRefillInventory = false
      this.showTable = true
      this.selectedOrderData = null
      this.fetchInventoryOrder()
    },
    toggleshowRefillInventory() {
      this.$emit('request-add-order')
    },
    async fetchInventoryOrder() {
      this.loading = true
      const { page, itemsPerPage } = this.options
      const params = { page, limit: itemsPerPage }
      if (this.filterPartName) {
        params.productSearch = this.filterPartName
      }
      if (this.filterOrder) {
        params.search = this.filterOrder
      }
      if (this.selectedOrderStatus) {
        params.orderStatus = this.selectedOrderStatus
      }
      const { data } = await this.$axios.get('/ecom/inventory/orders', {
        params: params
      })

      if (data.success) {
        this.inventoryOrders = data.inventoryOrders
        this.totalItems = data.pagination.totalCount
        this.loading = false
      }
    },
    async onStatusChange(status, id) {
      try {
        const selectedItem = this.inventoryOrders.find((item) => item._id === id)

        const partnerId = selectedItem.partner?._id
        const productId = selectedItem.product?._id
        const orderDeliveryLocation = selectedItem.orderDeliveryLocation
        const orderNumber = selectedItem.orderNumber

        const dataToSend = {
          orderStatus: status,
          partnerId: partnerId,
          productId: productId,
          orderDeliveryLocation: orderDeliveryLocation,
          orderNumber: orderNumber
        }
        const response = await this.$axios.put(`/ecom/inventory/order/${id}`, dataToSend)
        this.fetchInventoryOrder()
        const successMessage = response?.data?.message || 'Order status updated successfully !!'
        this.$toast.success(successMessage)
      } catch (error) {
        const errorMessage = error.response.data.message || 'Failed to update order status.'
        this.$toast.error(errorMessage)
      }
    },
    handleShowDialogDelete() {
      // Only show the dialog if there are selected items
      if (this.selected.length > 0) {
        this.showDeleteDialog = true
      }
    },
    orderEdit(item) {
      this.$emit('request-edit-order', item)
    },
    handleInput: debounce(function () {
      this.options.page = 1
      this.fetchInventoryOrder()
    }, 1000),
    async deleteSelectedOrders() {
      this.deleteLoading = true
      try {
        const deletePromises = this.selected.map((order) =>
          this.$axios.delete(`/ecom/inventory/orders/${order._id}`)
        )

        await Promise.all(deletePromises)
        this.$toast.success('Selected orders deleted successfully')
        this.selected = []
        this.showDeleteDialog = false
        this.fetchInventoryOrder()
      } catch (error) {
        console.error('Error deleting orders:', error)
        this.$toast.error(
          'Error deleting orders: ' + (error.response?.data?.message || error.message)
        )
      } finally {
        this.deleteLoading = false
      }
    },
    cancelDelete() {
      this.showDeleteConfirmation = false
      this.storeToDelete = null
      this.deleteLoading = false
    },
    async performDeleteStore() {
      // This method is referenced in the AlertPopUp but not implemented
      // It seems to be for deleting a single store, not related to orders
      // We'll implement a basic version but it might need to be adjusted based on actual requirements
      if (!this.storeToDelete) return

      this.deleteLoading = true
      try {
        const response = await this.$axios.delete(
          `${axiosUrl.stores.deleteStores}/${this.storeToDelete._id}`
        )
        this.$toast.success('Store deleted successfully')
        this.cancelDelete()
        this.fetchInventoryOrder()
      } catch (error) {
        console.error('Error deleting store:', error)
        this.$toast.error(
          'Error deleting store: ' + (error.response?.data?.message || error.message)
        )
      } finally {
        this.deleteLoading = false
      }
    }
  }
}
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper tbody tr.v-data-table__expanded__content {
  box-shadow: none;
}
::v-deep .v-data-table--fixed-header > .v-data-table__wrapper > table > thead > tr > th {
  padding: 10px;
  align-items: center !important;
}
.colom {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
}
.table-radius {
  border-radius: 0px !important;
}
/* .v-data-table >>> .v-data-table__wrapper > table > thead > tr > th {
  font-size: 0.875rem;
  color: #000;
} */
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
